/**
 * Fiş resmini termal yazıcının diline (ESC/POS) çevirir (20 Eki 2026).
 *
 * Yazı olarak değil resim olarak basılıyor: yazıcıların Türkçe karakter
 * tablosu markadan markaya değişiyor (ş, ğ, İ yanlış çıkıyor), fişin
 * tasarımı da (yazı boyu, çizgiler, kutular) Ayarlar > Fiş'ten geliyor ve
 * yazı komutlarıyla aynısı kurulamıyor. "GS v 0" raster komutunu Epson,
 * Xprinter, Rongta, Sewoo gibi bütün yaygın fiş yazıcıları anlıyor.
 */

/** Kağıda basılabilen genişlik, nokta (203 dpi'de 8 nokta/mm). */
export const PRINT_DOTS: Record<80 | 58, number> = { 80: 576, 58: 384 }

/** Siyah beyaz, satır satır paketlenmiş resim: her bayt yan yana 8 nokta. */
export interface MonoImage {
  widthBytes: number
  height: number
  bits: Uint8Array
}

/**
 * RGBA pikselleri siyah beyaza çevirir. Eşikten koyu olan nokta basılır;
 * saydam piksel kağıt (beyaz) sayılıyor.
 */
export function toMono(rgba: Uint8ClampedArray, width: number, height: number, threshold = 160): MonoImage {
  const widthBytes = Math.ceil(width / 8)
  const bits = new Uint8Array(widthBytes * height)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const a = rgba[i + 3] / 255
      // Saydamı beyazın üstüne koyarak parlaklık.
      const lum = (0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2]) * a + 255 * (1 - a)
      if (lum < threshold) bits[y * widthBytes + (x >> 3)] |= 0x80 >> (x & 7)
    }
  }
  return { widthBytes, height, bits }
}

/** Bir seferde gönderilen satır sayısı: ucuz yazıcıların tamponu küçük. */
const BAND = 128

/** Resmi GS v 0 komutlarına böler. */
export function rasterCommands(img: MonoImage): Uint8Array {
  const parts: Uint8Array[] = []
  for (let y = 0; y < img.height; y += BAND) {
    const h = Math.min(BAND, img.height - y)
    const head = Uint8Array.of(
      0x1d, 0x76, 0x30, 0x00,
      img.widthBytes & 0xff, (img.widthBytes >> 8) & 0xff,
      h & 0xff, (h >> 8) & 0xff,
    )
    parts.push(head, img.bits.subarray(y * img.widthBytes, (y + h) * img.widthBytes))
  }
  return concat(parts)
}

const INIT = Uint8Array.of(0x1b, 0x40) // ESC @
const FEED = Uint8Array.of(0x1b, 0x64, 0x03) // ESC d 3: kesiciye kadar ilerlet
const CUT = Uint8Array.of(0x1d, 0x56, 0x01) // GS V 1: kısmi kesim

/** Her resim ayrı fiş: arkasından ilerletip kesiyor. */
export function buildJob(images: MonoImage[]): Uint8Array {
  const parts: Uint8Array[] = [INIT]
  for (const img of images) parts.push(rasterCommands(img), FEED, CUT)
  return concat(parts)
}

export function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0))
  let o = 0
  for (const p of parts) {
    out.set(p, o)
    o += p.length
  }
  return out
}
